<template>
  <div id="node-tree">
    <div class="data-models">
      <v-select
        :clearable="false"
        :options="modelOptions"
        :searchable="false"
        label="name"
        v-model="selectedDataModel"
        v-if="modelOptions.length"
      />
    </div>
    <template v-if="dataModelId">
      <SearchBar v-model="search" />
      <Tree
        v-if="visible"
        :tree="stateTree"
        @toggle-node-opening="toggleExpandState"
        @toggle-node-selection="toggleSelectState"
        @toggle-parameter="toggleSelectState"
      />
    </template>
  </div>
</template>

<script>
import vSelect from "vue-select";
import { mapState, mapGetters, mapActions } from "vuex";
import TreeMixin from "./TreeMixin";
import SearchBar from "./SearchBar";
import config from "@/config.json";

const CODE_VECTOR_MISSING = "other";

export default {
  components: {
    SearchBar,
    vSelect,
  },

  mixins: [TreeMixin],
  data() {
    return {
      mode: "MEASURES",
      modelOptions: [],
      selectedDataModel: null,
    };
  },
  created() {
    this.$store.dispatch("sites/dataModels", this.$store.state.user.customerGuid).then((models) => {
      this.modelOptions = models;
      this.selectedDataModel = models.length ? models[0] : null;
    });
  },
  computed: {
    dataModelId() {
      return this.selectedDataModel ? this.selectedDataModel.id : null;
    },
    ...mapGetters({
      getAllAvailableChannels: "getAllAvailableChannels",
      getMeasureTypeByCode: "charts/getMeasureTypeByCode",
      getVectorName: "vectors/getName",
    }),
  },
  methods: {
    ...mapActions({
      clearAllSelectedSources: "charts/clearAllSelectedSources",
    }),
    checkChannel(channelCode) {
      return !!this.getAllAvailableChannels.find((stateChannel) => stateChannel.code === channelCode);
    },

    buildTree() {
      const localTree = {
        name: "Siti",
        selectable: false,
        visible: true,
        open: true,
        code: "__sites__",
        children: [],
      };

      if (this.sites.length > 1) {
        localTree.children = this.sites.map((site) => {
          const s = _.cloneDeep(site);
          const node = {
            name: s.name,
            code: s.guid,
            key: s.guid,
            type: "site",
            selectable: false,
            selected: false,
            open: !this.reportMode && this.expandedNodes.includes(s.guid),
            visible: true,
            children: this.getChildrenForSite(site),
            parameters: [],
          };

          return node;
        });
      } else {
        localTree.children = this.getChildrenForSite(this.sites[0]);
      }

      this.$store.commit("charts/setTree", localTree);
    },

    getChildrenForSite(userSite) {
      const children = [];
      const site = _.cloneDeep(userSite);
      const modelChannels = site.channels.filter((item) => item.data_model_id == this.dataModelId);
      const modelChannelCodes = modelChannels.map((channel) => channel.code);
      const rootChannels = modelChannels.filter((channel) => {
        return !channel.parent_code || !modelChannelCodes.includes(channel.parent_code);
      });

      const groupedChannelsWithVector = [];
      const channelsWithoutVector = [];

      rootChannels.forEach((channel) => {
        // const measureTypes = this.getMeasureTypeByCode(channel.code);

        const { vector } = channel;

        if (!vector) {
          channelsWithoutVector.push(channel);
          return;
        }

        const channelGroup = groupedChannelsWithVector.find((group) => group.code === vector);

        if (channelGroup) {
          channelGroup.channels.push(channel);
          return;
        }

        groupedChannelsWithVector.push({
          name: this.getVectorName(vector),
          code: vector,
          channels: [channel],
        });
      });

      groupedChannelsWithVector.forEach((groupedChannel) => {
        const code = `${userSite.guid}-${groupedChannel.code}`;
        children.push({
          name: groupedChannel.name,
          code,
          key: code,
          type: "group",
          selectable: false,
          selected: false,
          open: !this.reportMode && this.expandedNodes.includes(code),
          visible: true,
          children: this.sortChannelsByCategory(groupedChannel.channels).map((channel) =>
            this.children(_.cloneDeep(channel), site)
          ),
          parameters: [],
        });
      });

      const showChannelsWithoutVector = config.sideMenu.show_channels_without_vector;
      if (channelsWithoutVector.length && showChannelsWithoutVector) {
        const code = `${userSite.guid}-${CODE_VECTOR_MISSING}`;
        children.push({
          name: "Altri",
          code,
          key: code,
          type: "group",
          selectable: false,
          selected: false,
          open: !this.reportMode && this.expandedNodes.includes(code),
          visible: true,
          children: this.sortChannelsByCategory(channelsWithoutVector).map((c) => this.children(c, site)),
          parameters: [],
        });
      }

      if (site.parameters && site.parameters.length) {
        let sonParameters = [];
        let fatherParameters = [];

        site.parameters.forEach((item) => {
          if (!item.parent_code || item.parent_code == "") {
            fatherParameters.push(item);
            return;
          }
          sonParameters.push(item);
        });

        const code = `${userSite.guid}-parameters`;

        children.push({
          name: "Parametri",
          code,
          key: code,
          type: "group",
          selectable: false,
          selected: false,
          open: !this.reportMode && this.expandedNodes.includes(code),
          visible: true,
          children: fatherParameters.map((p) => this.childrenParameter(p, site)),
          parameters: [],
        });
      }

      return children;
    },

    childrenParameter(parameter, site) {
      const children = site.parameters.filter((c) => c.parent_code === parameter.code);

      parameter.selected = this.selectedSources.some((ss) => ss.code === parameter.code);
      parameter.selectable = children.length == 0 || parameter.formula;
      parameter.key = parameter.code;
      parameter.type = "parameter";
      parameter.open = !this.reportMode && this.expandedNodes.includes(parameter.code);
      parameter.visible = parameter.visible !== 0;
      parameter.children = site.parameters.filter((c) => c.parent_code === parameter.code);
      parameter.children.forEach((c) => this.childrenParameter(c, site));

      return parameter;
    },

    sortChannelsByCategory(channels) {
      return channels.sort(function (a, b) {
        return a.category ? a.category - b.category : 1;
      });
    },

    children(channel, site) {
      const { code } = channel;

      channel.code = code;
      channel.open = !this.reportMode && this.expandedNodes.includes(code);
      channel.selectable = true;
      channel.selected = this.selectedSources.some((ss) => ss.code === code);
      channel.type = "channel";
      channel.visible = channel.visible !== 0;
      channel.children = this.sortChannelsByCategory(
        site.channels.filter((item) => item.data_model_id == this.dataModelId).filter((c) => c.parent_code === code)
      );
      channel.children.forEach((c) => this.children(c, site));

      return channel;
    },
  },
  watch: {
    selectedDataModel() {
      this.$store.dispatch("charts/setModelId", this.dataModelId);
      this.buildTree();
      this.clearAllSelectedSources();
    },
  },
};
</script>
<style>
#node-tree .data-models {
  margin: 30px 30px 15px 30px;
}
#node-tree .v-select {
  background: #b54141;
}
#node-tree .vs__selected,
#node-tree .vs__dropdown-toggle {
  border-radius: 0;
}
#node-tree .vs__dropdown-toggle {
  background: #b54141;
  border: 1px solid #ffb2b2;
}
#node-tree .vs__actions {
  color: #fff;
}
#node-tree .vs__selected {
  color: #fff;
}
#node-tree .vs__open-indicator {
  fill: #fff;
}
#node-tree .vs__dropdown-option--highlight {
  background: #d16565;
}
#node-tree .vs1__combobox {
  height: 35px;
}
</style>



// WEBPACK FOOTER //
// src/components/Charts/Tree/MeasuresTree.vue